import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import { convertListingTypeToProductType, getListingPdpPath } from "@/utils/productTypes";
import type { Models } from "api/types";
import { hasLinearPricing } from "services/ListingHelperService";

/**
 * When Algolia cannot answer, the shop answers the same queries from the database: `GET /listings` (the patient's
 * visible catalog at their location, already member-priced) turned into search records, and every query the app
 * sends evaluated against them. Only the filters, facets, sorts and text match this app issues are supported.
 */

/** How long a search may take before the app stops waiting for Algolia and answers from the database. */
export const ALGOLIA_FALLBACK_AFTER_MS = 6000;

/**
 * Once Algolia has failed, every search is answered from the database for this long before Algolia is tried
 * again (the Algolia client itself keeps a failed host down for two minutes). Staying on one source keeps "Load
 * more" pages consistent and stops each keystroke waiting out the deadline again.
 */
export const ALGOLIA_RETRY_AFTER_MS = 2 * 60 * 1000;

/** A search record built from the database, carrying the member price `GET /listings` already computed. */
export type FallbackSearchRecord = AlgoliaSearchResult;

export type FallbackSources = {
  platformId: string;
  listings: readonly Models<"ListingModel">[];
  concerns: readonly Models<"ConcernModel">[];
  customCategories: readonly Models<"CategoryListingsResult">[];
  memberships: readonly Models<"MembershipModel">[];
  /** Empty where the practice has gift cards turned off. */
  giftCards: readonly Models<"DisplayGiftCardListing">[];
};

/**
 * What `fetch` rejects with when the network fails, per engine (Chrome, Safari, Firefox, React Native). Any
 * other `TypeError` is a bug in the app, not an outage.
 */
const NETWORK_FAILURE_MESSAGES = [
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource",
  "Network request failed",
];

/** Errors that mean Algolia is unreachable or failing, as opposed to a query the app got wrong. */
export const isAlgoliaOutage = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === "RetryError" || error.name === "DeserializationError") return true;
  if (error.name === "AlgoliaFallbackTimeout") return true;
  if (error instanceof TypeError) {
    return NETWORK_FAILURE_MESSAGES.some((message) => error.message.startsWith(message));
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" && (status >= 500 || status === 429);
};

const categoryPlacements = (customCategories: FallbackSources["customCategories"]) => {
  const placements = new Map<
    string,
    { categoryIds: string[]; categoryOrder: Record<string, number> }
  >();
  for (const { category, categoryInclusions } of customCategories) {
    for (const inclusion of categoryInclusions) {
      const id = inclusion.packageListingId ?? inclusion.membershipId;
      if (!id) continue;
      const placement = placements.get(id) ?? { categoryIds: [], categoryOrder: {} };
      placement.categoryIds.push(category.id);
      placement.categoryOrder[category.id] = inclusion.order;
      placements.set(id, placement);
    }
  }
  return placements;
};

/** The open ends of a sale window, as the index writes them (`SearchRecordTimestamps`). */
const OPEN_START = 0;
const OPEN_END = 253402300799;

/** A `yyyy-MM-dd` date at this wall-clock time in the patient's time zone, as Unix seconds. */
const localUnixSeconds = (date: string, hours: number, minutes: number, seconds: number) => {
  const [year, month, day] = date.split("-").map(Number);
  return Math.floor(new Date(year, month - 1, day, hours, minutes, seconds).valueOf() / 1000);
};

/**
 * A gift card's sale window as the index records it: from the start of its start date to the end of its end
 * date. The index resolves those days in the practice's location time zones, which `GET /giftcards` does not
 * carry, so here they are resolved in the patient's own time zone — the one they shop from, and the practice's
 * for nearly every patient.
 */
const giftCardSaleWindow = ({ startDate, endDate }: Models<"DisplayGiftCardListing">) => ({
  availableFrom: startDate ? localUnixSeconds(startDate, 0, 0, 0) : OPEN_START,
  availableUntil: endDate ? localUnixSeconds(endDate, 23, 59, 59) : OPEN_END,
});

export const toFallbackRecords = ({
  platformId,
  listings,
  concerns,
  customCategories,
  memberships,
  giftCards,
}: FallbackSources): FallbackSearchRecord[] => {
  const concernNames = new Map(concerns.map(({ id, name }) => [id, name ?? ""]));
  const placements = categoryPlacements(customCategories);
  const shared = (id: string, locationIds: readonly string[]) => ({
    platformId,
    // No locations means sold everywhere, as the index records it.
    locationIds: [...locationIds],
    allLocations: locationIds.length === 0,
    availableFrom: OPEN_START,
    availableUntil: OPEN_END,
    isVisibleInShop: true,
    categoryIds: placements.get(id)?.categoryIds ?? [],
    categoryOrder: placements.get(id)?.categoryOrder ?? {},
  });

  const listingRecords = listings.map(
    (listing, index): FallbackSearchRecord => ({
      // `GET /listings` is already narrowed to the patient's location.
      ...shared(listing.id, []),
      objectID: listing.id,
      title: listing.title,
      description: listing.description,
      imageUrl: listing.imageUrl,
      unitName: listing.packageUnitType?.singularUnitType ?? null,
      hasLinearPricing: hasLinearPricing(
        listing.packages.flatMap(({ packagePrices }) => packagePrices),
      ),
      productType: convertListingTypeToProductType(listing.type),
      concernIds: listing.concernIds,
      concernNames: listing.concernIds.map((id) => concernNames.get(id) ?? "").filter(Boolean),
      bodyAreaIds: listing.areaIds,
      bodyAreaNames: [],
      createdTimestamp: new Date(listing.created).valueOf() / 1000,
      // `GET /listings` answers in the practice's shop display order.
      defaultOrder: index,
      minBasePrice: listing.baseCost,
      saleOrder: 0,
      sellVolume: listing.sellVolume,
      requires: {
        skinDrop: listing.type === "ProductFromExternalSource",
        myProducts: listing.type === "Product",
      },
      packageIds: listing.packages.map(({ id }) => id),
      pdpPath: getListingPdpPath(listing.id, listing.type),
      databaseMemberPrice: listing.membershipCost ?? null,
    }),
  );

  // Custom categories render memberships and gift cards from their own endpoints; search renders them from the
  // record. The index holds only the listed ones.
  const membershipRecords = memberships
    .filter(({ active, unlisted }) => active && !unlisted)
    .map(
      (membership, index): FallbackSearchRecord => ({
        ...shared(membership.id, membership.platformLocationsIds),
        objectID: membership.id,
        title: membership.name,
        description: membership.description ?? "",
        imageUrl: membership.imageUrl ?? null,
        hasLinearPricing: false,
        productType: convertListingTypeToProductType("Membership"),
        concernIds: [],
        concernNames: [],
        bodyAreaIds: [],
        bodyAreaNames: [],
        createdTimestamp: new Date(membership.created).valueOf() / 1000,
        defaultOrder: listings.length + index,
        minBasePrice: membership.monthlyCost,
        saleOrder: 0,
        sellVolume: 0,
        requires: { skinDrop: false, myProducts: false },
        packageIds: [],
        pdpPath: getListingPdpPath(membership.id, "Membership"),
        databaseMemberPrice: null,
      }),
    );

  const giftCardRecords = giftCards
    .filter(({ isActive }) => isActive)
    .map(
      (giftCard, index): FallbackSearchRecord => ({
        ...shared(giftCard.id, giftCard.locationIds),
        ...giftCardSaleWindow(giftCard),
        objectID: giftCard.id,
        title: giftCard.name.text,
        description: giftCard.description.text,
        imageUrl: giftCard.imageUri,
        hasLinearPricing: false,
        productType: convertListingTypeToProductType("GiftCard"),
        concernIds: [],
        concernNames: [],
        bodyAreaIds: [],
        bodyAreaNames: [],
        createdTimestamp: new Date(giftCard.createdAt).valueOf() / 1000,
        defaultOrder: listings.length + membershipRecords.length + index,
        minBasePrice: Math.min(...giftCard.giftCards.map(({ baseCostInCents }) => baseCostInCents)),
        saleOrder: 0,
        sellVolume: 0,
        requires: { skinDrop: false, myProducts: false },
        packageIds: giftCard.giftCards.map(({ packageId }) => packageId),
        pdpPath: getListingPdpPath(giftCard.id, "GiftCard"),
        databaseMemberPrice: null,
      }),
    );

  return [...listingRecords, ...membershipRecords, ...giftCardRecords];
};

// ---------------------------------------------------------------------------------------------------------------
// Filters: the grammar `buildCatalogFilters` / `objectIdFilter` produce.

type ComparisonOperator = "<" | "<=" | "=" | ">=" | ">";

type FilterNode =
  | { kind: "match"; attribute: string; value: string }
  | { kind: "compare"; attribute: string; operator: ComparisonOperator; value: number }
  | { kind: "not"; operand: FilterNode }
  | { kind: "and" | "or"; operands: FilterNode[] };

const COMPARISON = /^([A-Za-z_.]+)\s*(<=|>=|<|>|=)\s*(-?\d+(?:\.\d+)?)$/;

const TOKEN =
  /\s*(\(|\)|AND\b|OR\b|NOT\b|[A-Za-z_.]+\s*(?:<=|>=|<|>|=)\s*-?\d+(?:\.\d+)?|[A-Za-z_.]+:(?:"(?:[^"\\]|\\.)*"|[^\s()]+))/y;

const tokenize = (filters: string): string[] => {
  const tokens: string[] = [];
  let position = 0;
  while (filters.slice(position).trim()) {
    TOKEN.lastIndex = position;
    const match = TOKEN.exec(filters);
    if (!match) throw new Error(`Search fallback cannot read the filter: ${filters}`);
    tokens.push(match[1]);
    position = TOKEN.lastIndex;
  }
  return tokens;
};

const unquote = (value: string) =>
  value.startsWith('"') ? value.slice(1, -1).replace(/\\(.)/g, "$1") : value;

export const parseFilters = (filters: string): FilterNode | null => {
  const tokens = tokenize(filters);
  let position = 0;

  const parseOr = (): FilterNode => {
    const operands = [parseAnd()];
    while (tokens[position] === "OR") {
      position++;
      operands.push(parseAnd());
    }
    return operands.length === 1 ? operands[0] : { kind: "or", operands };
  };
  const parseAnd = (): FilterNode => {
    const operands = [parseUnary()];
    while (tokens[position] === "AND") {
      position++;
      operands.push(parseUnary());
    }
    return operands.length === 1 ? operands[0] : { kind: "and", operands };
  };
  const parseUnary = (): FilterNode => {
    const token = tokens[position++];
    if (token === "NOT") return { kind: "not", operand: parseUnary() };
    if (token === "(") {
      const inner = parseOr();
      if (tokens[position++] !== ")")
        throw new Error(`Search fallback: unbalanced filter ${filters}`);
      return inner;
    }
    const comparison = token ? COMPARISON.exec(token) : null;
    if (comparison) {
      return {
        kind: "compare",
        attribute: comparison[1],
        operator: comparison[2] as ComparisonOperator,
        value: Number(comparison[3]),
      };
    }
    const separator = token?.indexOf(":") ?? -1;
    if (separator < 0) throw new Error(`Search fallback cannot read the filter: ${filters}`);
    return {
      kind: "match",
      attribute: token.slice(0, separator),
      value: unquote(token.slice(separator + 1)),
    };
  };

  if (tokens.length === 0) return null;
  const tree = parseOr();
  if (position !== tokens.length)
    throw new Error(`Search fallback cannot read the filter: ${filters}`);
  return tree;
};

const attributeValues = (record: FallbackSearchRecord, attribute: string): unknown[] => {
  const value = attribute
    .split(".")
    .reduce<unknown>(
      (current, key) => (current as Record<string, unknown> | undefined)?.[key],
      record,
    );
  return Array.isArray(value) ? value : [value];
};

/** Algolia matches filter and facet values without regard to case (ids reach filters in either case). */
const sameValue = (value: unknown, expected: string) =>
  String(value).toLowerCase() === expected.toLowerCase();

const compare = (actual: number, operator: ComparisonOperator, expected: number) => {
  switch (operator) {
    case "<":
      return actual < expected;
    case "<=":
      return actual <= expected;
    case "=":
      return actual === expected;
    case ">=":
      return actual >= expected;
    case ">":
      return actual > expected;
  }
};

const matches = (record: FallbackSearchRecord, node: FilterNode | null): boolean => {
  if (!node) return true;
  switch (node.kind) {
    case "match":
      return attributeValues(record, node.attribute).some((value) => sameValue(value, node.value));
    case "compare":
      return attributeValues(record, node.attribute).some(
        (value) => typeof value === "number" && compare(value, node.operator, node.value),
      );
    case "not":
      return !matches(record, node.operand);
    case "and":
      return node.operands.every((operand) => matches(record, operand));
    case "or":
      return node.operands.some((operand) => matches(record, operand));
  }
};

type FacetFilters = string | readonly (string | readonly string[])[] | undefined;

const matchesFacetFilters = (record: FallbackSearchRecord, facetFilters: FacetFilters): boolean => {
  const groups = typeof facetFilters === "string" ? [facetFilters] : (facetFilters ?? []);
  return groups.every((group) =>
    (typeof group === "string" ? [group] : group).some((clause) => {
      const negated = clause.startsWith("-");
      const [attribute, ...rest] = (negated ? clause.slice(1) : clause).split(":");
      const hit = attributeValues(record, attribute).some((value) =>
        sameValue(value, rest.join(":")),
      );
      return negated ? !hit : hit;
    }),
  );
};

// ---------------------------------------------------------------------------------------------------------------
// Queries

export type FallbackRequest = {
  indexName: string;
  query?: string;
  filters?: string;
  facetFilters?: FacetFilters;
  facets?: string | readonly string[];
  hitsPerPage?: number;
  page?: number;
};

const byIndex = (
  indexName: string,
): ((a: FallbackSearchRecord, b: FallbackSearchRecord) => number) => {
  if (indexName.endsWith("_best_selling")) return (a, b) => b.sellVolume - a.sellVolume;
  if (indexName.endsWith("_price_asc")) return (a, b) => a.minBasePrice - b.minBasePrice;
  if (indexName.endsWith("_price_desc")) return (a, b) => b.minBasePrice - a.minBasePrice;
  if (indexName.endsWith("_recent")) return (a, b) => b.createdTimestamp - a.createdTimestamp;
  return (a, b) => a.defaultOrder - b.defaultOrder;
};

const matchesQuery = (record: FallbackSearchRecord, query: string | undefined) => {
  const term = query?.trim().toLowerCase();
  if (!term) return true;
  return [record.title, record.description, ...record.concernNames].some((text) =>
    text?.toLowerCase().includes(term),
  );
};

const countFacets = (hits: readonly FallbackSearchRecord[], facets: FallbackRequest["facets"]) => {
  const attributes = typeof facets === "string" ? [facets] : (facets ?? []);
  return Object.fromEntries(
    attributes.map((attribute) => {
      const counts: Record<string, number> = {};
      for (const hit of hits) {
        for (const value of attributeValues(hit, attribute)) {
          if (value === undefined || value === null) continue;
          counts[String(value)] = (counts[String(value)] ?? 0) + 1;
        }
      }
      return [attribute, counts];
    }),
  );
};

type SearchArgument =
  | readonly { indexName: string; params?: Record<string, unknown> }[]
  | { requests: readonly ({ indexName: string } & Record<string, unknown>)[] };

const toFallbackRequests = (argument: SearchArgument): FallbackRequest[] =>
  Array.isArray(argument)
    ? argument.map(({ indexName, params }) => ({ indexName, ...params }))
    : (argument as Extract<SearchArgument, { requests: unknown }>).requests.map((request) => ({
        ...request,
      }));

const withDeadline = async <T>(work: Promise<T>, milliseconds: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const timeout = new Error(`Algolia did not answer within ${milliseconds} ms`);
      timeout.name = "AlgoliaFallbackTimeout";
      reject(timeout);
    }, milliseconds);
  });
  try {
    return await Promise.race([work, deadline]);
  } finally {
    clearTimeout(timer);
  }
};

const answerFromDatabase = async (
  loadRecords: () => Promise<readonly FallbackSearchRecord[]>,
  argument: SearchArgument,
) => {
  const records = await loadRecords();
  return {
    results: toFallbackRequests(argument).map((request) => answerFromRecords(records, request)),
  };
};

/**
 * A search client for an environment with no Algolia index (a snapshot or local stack whose backend searches in
 * memory): every search is answered from the database records, the same way an Algolia outage is.
 */
export const databaseCatalogClient = (
  loadRecords: () => Promise<readonly FallbackSearchRecord[]>,
) => ({
  appId: "",
  transporter: undefined,
  search: (argument: SearchArgument) => answerFromDatabase(loadRecords, argument),
});

/**
 * The same client, except that a search Algolia cannot answer (unreachable, 5xx, rate-limited, or slower than
 * `ALGOLIA_FALLBACK_AFTER_MS`) is answered from the database records instead, and so is every search for the next
 * `ALGOLIA_RETRY_AFTER_MS`. A query the app got wrong (4xx) still
 * fails loudly. If the database cannot answer either, the Algolia error is what callers see.
 */
export const withDatabaseFallback = <
  Client extends { search: (...args: never[]) => Promise<unknown> },
>(
  client: Client,
  loadRecords: () => Promise<readonly FallbackSearchRecord[]>,
  onFallback: (error: unknown) => void,
): Client => {
  let databaseUntil = 0;

  const search = async (argument: SearchArgument, ...rest: unknown[]) => {
    if (Date.now() < databaseUntil) {
      return answerFromDatabase(loadRecords, argument);
    }
    try {
      return await withDeadline(
        (client.search as (...args: unknown[]) => Promise<unknown>)(argument, ...rest),
        ALGOLIA_FALLBACK_AFTER_MS,
      );
    } catch (error) {
      if (!isAlgoliaOutage(error)) throw error;
      databaseUntil = Date.now() + ALGOLIA_RETRY_AFTER_MS;
      onFallback(error);
      return await answerFromDatabase(loadRecords, argument).catch(() => {
        throw error;
      });
    }
  };
  return { ...client, search } as Client;
};

export const answerFromRecords = (
  records: readonly FallbackSearchRecord[],
  request: FallbackRequest,
) => {
  const tree = request.filters ? parseFilters(request.filters) : null;
  const matched = records
    .filter((record) => matches(record, tree))
    .filter((record) => matchesFacetFilters(record, request.facetFilters))
    .filter((record) => matchesQuery(record, request.query))
    .sort(byIndex(request.indexName));

  const hitsPerPage = request.hitsPerPage ?? 20;
  const page = request.page ?? 0;
  const hits = hitsPerPage > 0 ? matched.slice(page * hitsPerPage, (page + 1) * hitsPerPage) : [];

  return {
    hits,
    nbHits: matched.length,
    page,
    nbPages: hitsPerPage > 0 ? Math.ceil(matched.length / hitsPerPage) : 0,
    hitsPerPage,
    facets: countFacets(matched, request.facets),
    exhaustiveNbHits: true,
    processingTimeMS: 0,
    query: request.query ?? "",
    params: "",
    index: request.indexName,
  };
};
